import { useEffect, useState } from 'react';
import { X, Loader2, ShieldCheck } from 'lucide-react';
import { DA, SunOrnament, SabrilDecorFrame } from '../theme/sabril-da';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'FR', label: '🇫🇷 France' },
  { code: 'MA', label: '🇲🇦 Maroc' },
  { code: 'ES', label: '🇪🇸 Espagne' },
];

// TTC price per country — MUST match backend economie_price_for()
const ECONOMIE_PRICE_BY_COUNTRY = { FR: 2790, MA: 2190, ES: 2790 };
const priceFor = (country) => ECONOMIE_PRICE_BY_COUNTRY[country] || 2790;
const fmtEur = (n) => new Intl.NumberFormat('fr-FR').format(n);

const STATUS_OPTIONS = [
  { code: 'existing', label: 'Déjà ouvert', sub: 'Restaurant en activité' },
  { code: 'opening',  label: 'Projet d\'ouverture', sub: 'En cours ou à venir' },
];

const PAYMENT_METHODS = [
  { code: 'online',  label: '🌐 En ligne', sub: 'Paiement sécurisé Stripe' },
  { code: 'on_site', label: '💵 Sur place', sub: 'Le jour de la mission' },
];

// Listen to a global event `economie:open` to open the modal from anywhere.
export default function EconomieCheckoutModal() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    restaurant_name: '',
    address: '',
    city: '',
    country: 'FR',
    status: 'existing',
    preferred_date: '',
    payment_method: 'online',
  });

  useEffect(() => {
    const handler = () => {
      setError('');
      setConfirmed(false);
      setOpen(true);
    };
    window.addEventListener('economie:open', handler);
    return () => window.removeEventListener('economie:open', handler);
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setError('');
    // Basic validation
    const required = ['firstName', 'lastName', 'email', 'phone', 'restaurant_name', 'address', 'city'];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) {
        setError('Remplis tous les champs pour continuer.');
        return;
      }
    }
    if (!form.email.includes('@')) {
      setError('Email invalide.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/economie/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin, ...form }),
      });
      const data = await res.json();
      if (res.ok && data) {
        if (form.payment_method === 'online' && data.url) {
          window.location.href = data.url;
          return;
        }
        if (form.payment_method === 'on_site') {
          setConfirmed(true);
          setLoading(false);
          return;
        }
      }
      setError('Impossible d\'enregistrer ta réservation. Réessaie ou contacte Sabril.');
    } catch (err) {
      setError('Erreur réseau. Réessaie.');
    }
    setLoading(false);
  };

  if (!open) return null;

  // Confirmation screen after successful on_site reservation
  if (confirmed) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) { setOpen(false); setConfirmed(false); } }}
        style={{ background: 'rgba(11,43,36,0.55)', backdropFilter: 'blur(8px)' }}
        data-testid="economie-modal-confirmed"
      >
        <div
          className="w-full sm:max-w-[440px] p-6 sm:p-8 text-center"
          style={{
            background: DA.ivory,
            borderRadius: '24px 24px 0 0',
            color: DA.greenDeep,
          }}
        >
          <div className="flex justify-center mb-4">
            <SunOrnament size={28} />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 mx-auto"
               style={{ background: DA.goldSofter, border: `1px solid ${DA.goldMain}` }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: DA.goldMain }} />
            <span style={{ color: DA.goldDeep, fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Réservation reçue</span>
          </div>
          <h2 style={{ fontFamily: DA.serif, fontSize: 28, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 12, color: DA.greenDeep }}>
            À bientôt <span style={{ color: DA.goldMain }}>{form.firstName}</span>.
          </h2>
          <p style={{ color: DA.inkMuted, fontSize: 13.5, lineHeight: 1.45, marginBottom: 24 }}>
            J&apos;ai bien reçu ta demande pour <strong style={{ color: DA.greenDeep }}>{form.restaurant_name}</strong>.
            Je te contacte sous 24h par WhatsApp au <strong style={{ color: DA.greenDeep }}>{form.phone}</strong> pour caler les dates et modalités du paiement sur place.
          </p>
          <div className="rounded-2xl px-4 py-3 mb-5 text-left" style={{ background: DA.ivoryDeep, border: `0.5px solid ${DA.goldSoft}` }}>
            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', color: DA.goldMain, fontFamily: DA.sans }}>Récapitulatif</p>
            <p style={{ color: DA.ink, fontSize: 13.5, marginTop: 8, lineHeight: 1.55, fontFamily: DA.sans }}>
              <span style={{ color: DA.goldMain }}>✈️</span> Classe Économie · 48h sur place<br/>
              <span style={{ color: DA.goldMain }}>📅</span> Date souhaitée : <strong>{form.preferred_date || 'à définir ensemble'}</strong><br/>
              <span style={{ color: DA.goldMain }}>💵</span> Paiement sur place · <strong>{fmtEur(priceFor(form.country))}€ TTC</strong>
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); setConfirmed(false); }}
            className="w-full py-3.5 rounded-xl font-bold transition active:scale-[0.98]"
            style={{
              background: DA.greenDeep,
              color: '#FFFFFF',
              fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase',
              fontFamily: DA.sans,
            }}
            data-testid="economie-confirmed-close"
          >
            J&apos;ai compris — à bientôt
          </button>
        </div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: '11px 13px',
    background: DA.paper,
    border: `1px solid ${DA.goldSoft}`,
    borderRadius: 10,
    fontSize: 14.5,
    fontFamily: DA.sans,
    color: DA.greenDeep,
    outline: 'none',
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#5A5A5A',
    marginBottom: 6,
    display: 'block',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      data-testid="economie-modal"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="w-full sm:max-w-[480px] max-h-[92vh] overflow-y-auto relative"
        style={{
          background: DA.ivory,
          borderRadius: '24px 24px 0 0',
          boxShadow: '0 -20px 60px -10px rgba(11,43,36,0.35)',
        }}
      >
        <SabrilDecorFrame awningTL awningBR={false} mimosa={false} palm awningSize={90} />
        {/* Header */}
        <div className="sticky top-0 z-10 px-5 pt-4 pb-3 flex items-start justify-between"
             style={{ background: `linear-gradient(180deg, ${DA.ivory} 92%, transparent 100%)` }}>
          <div className="flex-1 pr-3">
            <p style={{
              fontFamily: DA.sans, fontSize: 9.5,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: DA.goldMain, fontWeight: 800,
            }}>
              48H · EXPRESS
            </p>
            <h2 style={{
              fontFamily: DA.serif,
              fontSize: 26, fontWeight: 700,
              letterSpacing: '-0.02em', lineHeight: 1.05,
              color: DA.greenDeep, marginTop: 4,
            }}>
              Réserver mon audit express<span style={{ color: DA.goldMain }}>.</span>
            </h2>
            <p style={{ fontSize: 13, color: DA.inkMuted, marginTop: 6, lineHeight: 1.4, fontFamily: DA.sans }}>
              48h sur place · rapport stratégique · roadmap 90 jours · débrief dirigeant.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-full active:scale-95 transition flex-shrink-0"
            style={{ background: DA.ivoryDeep, color: DA.greenDeep, border: `0.5px solid ${DA.goldSoft}` }}
            aria-label="Fermer"
            data-testid="economie-modal-close"
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>

        {/* Price stripe — dynamique selon le pays sélectionné */}
        <div className="mx-5 mb-5 rounded-2xl px-4 py-3.5 flex items-center justify-between"
             style={{
               background: DA.greenDeep,
               boxShadow: `0 10px 24px -10px ${DA.greenDeep}88`,
             }}>
          <div>
            <p style={{ fontFamily: DA.sans, fontSize: 9.5,
                        letterSpacing: '0.22em', textTransform: 'uppercase',
                        color: DA.goldMain, fontWeight: 800 }}>
              Tarif TTC · {COUNTRIES.find((c) => c.code === form.country)?.label || form.country}
            </p>
            <p style={{ fontFamily: DA.serif, fontSize: 36, fontWeight: 700,
                        color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1, marginTop: 4 }}
               data-testid="economie-price-display">
              {fmtEur(priceFor(form.country))}€
            </p>
          </div>
          <ShieldCheck className="w-8 h-8" style={{ color: DA.goldMain }} strokeWidth={2} />
        </div>

        {/* Form */}
        <form onSubmit={submit} className="px-5 pb-8 space-y-3.5" data-testid="economie-form">
          {/* Prénom / Nom */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Prénom</label>
              <input
                type="text" value={form.firstName} onChange={set('firstName')}
                style={inputStyle} placeholder="Prénom" required
                data-testid="economie-firstName"
              />
            </div>
            <div>
              <label style={labelStyle}>Nom</label>
              <input
                type="text" value={form.lastName} onChange={set('lastName')}
                style={inputStyle} placeholder="Nom" required
                data-testid="economie-lastName"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label style={labelStyle}>Email</label>
            <input
              type="email" value={form.email} onChange={set('email')}
              style={inputStyle} placeholder="ton@email.com" required
              data-testid="economie-email"
            />
          </div>

          {/* Phone */}
          <div>
            <label style={labelStyle}>Téléphone (WhatsApp)</label>
            <input
              type="tel" value={form.phone} onChange={set('phone')}
              style={inputStyle} placeholder="+33 6 12 34 56 78" required
              data-testid="economie-phone"
            />
          </div>

          {/* Restaurant */}
          <div>
            <label style={labelStyle}>Nom du restaurant</label>
            <input
              type="text" value={form.restaurant_name} onChange={set('restaurant_name')}
              style={inputStyle} placeholder="Ex : Maison Avocado" required
              data-testid="economie-restaurant"
            />
          </div>

          {/* Address */}
          <div>
            <label style={labelStyle}>Adresse</label>
            <input
              type="text" value={form.address} onChange={set('address')}
              style={inputStyle} placeholder="Rue, avenue…" required
              data-testid="economie-address"
            />
          </div>

          {/* City + Country */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={labelStyle}>Ville</label>
              <input
                type="text" value={form.city} onChange={set('city')}
                style={inputStyle} placeholder="Ville" required
                data-testid="economie-city"
              />
            </div>
            <div>
              <label style={labelStyle}>Pays</label>
              <select
                value={form.country} onChange={set('country')}
                style={{ ...inputStyle, appearance: 'auto' }}
                data-testid="economie-country"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Statut */}
          <div>
            <label style={labelStyle}>Statut du restaurant</label>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, status: s.code }))}
                  className="text-left transition active:scale-[0.98]"
                  style={{
                    padding: '11px 12px',
                    borderRadius: 12,
                    border: form.status === s.code ? '2px solid #0F7A78' : '1px solid rgba(0,0,0,0.10)',
                    background: form.status === s.code ? 'rgba(15,122,120,0.08)' : '#FFFFFF',
                  }}
                  data-testid={`economie-status-${s.code}`}
                >
                  <p style={{ fontSize: 14, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                    {s.label}
                  </p>
                  <p style={{ fontSize: 11, color: '#5A5A5A', marginTop: 2 }}>
                    {s.sub}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Date souhaitée */}
          <div>
            <label style={labelStyle}>Date souhaitée (indicative — je m&apos;adapte)</label>
            <input
              type="date"
              value={form.preferred_date}
              onChange={set('preferred_date')}
              min={new Date().toISOString().split('T')[0]}
              style={inputStyle}
              data-testid="economie-date"
            />
            <p style={{ fontSize: 10.5, color: '#5A5A5A', marginTop: 4, lineHeight: 1.35 }}>
              📅 On confirme les dates ensemble par WhatsApp après réservation.
            </p>
          </div>

          {/* Payment method */}
          <div>
            <label style={labelStyle}>Mode de paiement</label>
            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_METHODS.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, payment_method: p.code }))}
                  className="text-left transition active:scale-[0.98]"
                  style={{
                    padding: '11px 12px',
                    borderRadius: 12,
                    border: form.payment_method === p.code ? '2px solid #0F7A78' : '1px solid rgba(0,0,0,0.10)',
                    background: form.payment_method === p.code ? 'rgba(15,122,120,0.08)' : '#FFFFFF',
                  }}
                  data-testid={`economie-payment-${p.code}`}
                >
                  <p style={{ fontSize: 14, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em' }}>
                    {p.label}
                  </p>
                  <p style={{ fontSize: 11, color: '#5A5A5A', marginTop: 2 }}>
                    {p.sub}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <p style={{ color: '#DC2626', fontSize: 12.5, fontWeight: 700 }}
               data-testid="economie-error">
              {error}
            </p>
          )}

          {/* CTA */}
          <button
            type="submit"
            disabled={loading}
            className="w-full active:scale-[0.98] transition disabled:opacity-70 flex items-center justify-center gap-2"
            style={{
              background: DA.greenDeep,
              color: '#FFFFFF',
              padding: '15px 20px',
              borderRadius: 12,
              fontFamily: DA.sans,
              fontSize: 13, fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              boxShadow: `0 12px 28px -8px ${DA.greenDeep}88`,
              border: 'none',
              cursor: loading ? 'default' : 'pointer',
              marginTop: 6,
            }}
            data-testid="economie-submit"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {form.payment_method === 'online' ? 'Redirection Stripe…' : 'Enregistrement…'}</>
            ) : form.payment_method === 'online' ? (
              <><span style={{ color: DA.goldMain }}>✈️</span> Payer {fmtEur(priceFor(form.country))}€ TTC →</>
            ) : (
              <><span style={{ color: DA.goldMain }}>✈️</span> Réserver · paiement sur place →</>
            )}
          </button>

          <p style={{ fontSize: 10.5, color: '#5A5A5A', textAlign: 'center', lineHeight: 1.4, marginTop: 4 }}>
            🔒 {form.payment_method === 'online'
              ? 'Paiement sécurisé Stripe · Sabril te contacte sous 24h pour caler les dates'
              : 'Aucun paiement maintenant · Sabril te contacte sous 24h pour caler les dates & modalités'}
          </p>
        </form>
      </div>
    </div>
  );
}
